import React from 'react';
import { ScrollView, StyleSheet, View, Linking, Platform } from 'react-native';
import { useTheme, FAB, Surface, DataTable, IconButton, Text, List, Divider, Title, Button, Chip } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useWeeklyReport } from '../hooks/useWeeklyReport';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import { getDailyDrivingReportPDF, getTimeLogs, type WeeklyDrivingReport } from '../api/apiService';
import { reportDayKey } from '../utils/reportDay';
import EditDayEventsModal from './EditDayEventsModal';
import { LoadingScreen } from './LoadingScreen';
import { isReportRecordEditingEnabled } from '../utils/featureToggles';
import { ReportWorkCompanies } from './ReportWorkCompanies';

export const DailyDrivingReport: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { report, loading, error, fetchReport } = useWeeklyReport();
  const [editVisible, setEditVisible] = React.useState(false);
  const [anyEdited, setAnyEdited] = React.useState(false);
  const [modalKey, setModalKey] = React.useState(0);

  // Use a stable value for today and run effects BEFORE any conditional returns to obey hooks rules.
  // The key must be built in the zone the backend groups `daily_hours` by, not
  // in UTC — see reportDay.ts (#300).
  const today = reportDayKey();

  React.useEffect(() => {
    const checkEdited = async () => {
      try {
        const resp = await getTimeLogs();
        if (resp.ok) {
          // `timestamp` is UTC ISO; comparing its prefix to a report-zone key
          // would disagree for the same hour window the key itself fixes (#300).
          const edited = resp.data.some(
            (e) => reportDayKey(new Date(e.timestamp)) === today && (e as any).edited,
          );
          setAnyEdited(edited);
        }
      } catch {}
    };
    checkEdited();
  }, [editVisible, today]);

  if (loading) {
    return <LoadingScreen fullScreen={false} />;
  }
  if (error) {
    return (
      <Surface style={[styles.loadingContainer, { backgroundColor: theme.colors.surface }]}>
        <Text variant="bodyLarge" style={{ color: theme.colors.error }}>
          {error}
        </Text>
      </Surface>
    );
  }
  if (!report) {
    return (
      <Surface style={[styles.loadingContainer, { backgroundColor: theme.colors.surface }]}>
        <Text variant="bodyLarge" style={{ color: theme.colors.onSurface }}>
          {t('noReport')}
        </Text>
      </Surface>
    );
  }

  const dailyData: WeeklyDrivingReport['daily_hours'][string] = report.daily_hours[today] || {
    total_driving_time: { hours: 0, minutes: 0 },
    total_availability_time: { hours: 0, minutes: 0 },
    total_rest_time: { hours: 0, minutes: 0 },
    vehicle_reg: null,
    odometer_start: null,
    odometer_end: null,
    events: [],
    observations: [],
  };

  const exportToPDF = async () => {
    try {
      const response = await getDailyDrivingReportPDF(today);
      if (!response.ok) throw new Error(`Erro na API: ${response.status}`);
      const blob = response.data;

      if (Platform.OS === 'web') {
        // Web platform handling
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `daily_driving_report_${today}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Native platform handling
        const fileUri = `${FileSystem.documentDirectory}daily_driving_report_${today}.pdf`;
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result?.toString().split(',')[1] || '');
          reader.readAsDataURL(blob);
        });
        await FileSystem.writeAsStringAsync(fileUri, base64, { encoding: FileSystem.EncodingType.Base64 });
        await Sharing.shareAsync(fileUri);
      }
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
      alert(t('errorExportingPDF'));
    }
  };

  const openGoogleMaps = (location: string | null) => {
    if (location) {
      const pointMatch = location.match(/POINT\s*\((-?\d+\.\d+)\s+(-?\d+\.\d+)\)/);
      if (pointMatch) {
        const [_, lon, lat] = pointMatch;
        if (lat && lon) {
          const url = `https://www.google.com/maps?q=${lat},${lon}`;
          Linking.openURL(url).catch((err) => console.error('Erro ao abrir Google Maps:', err));
        }
      } else {
        const [lat, lon] = location.split(',').map((coord) => coord.trim());
        if (lat && lon) {
          const url = `https://www.google.com/maps?q=${lat},${lon}`;
          Linking.openURL(url).catch((err) => console.error('Erro ao abrir Google Maps:', err));
        }
      }
    }
  };


  return (
    <Surface style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Detalhes do Condutor */}
        <View style={styles.section}>
          <Title style={{ color: theme.colors.primary, fontSize: 20, fontWeight: '600' }}>
            {t('driverDetails')}
          </Title>
          <Divider style={[styles.divider, { backgroundColor: theme.colors.outline }]} />
          <List.Item
            title={t('name')}
            description={report.driver_details.name}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('email')}
            description={report.driver_details.email}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('phoneNumber')}
            description={report.driver_details.phone_number || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('dateOfBirth')}
            description={report.driver_details.date_of_birth || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('nif')}
            description={report.driver_details.nif || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('address')}
            description={report.driver_details.address || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('licenseNumber')}
            description={report.driver_details.license_number || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
        </View>

        {/* Detalhes da Empresa */}
        {dailyData.attributions?.length ? <View style={styles.section}>
          <Text variant="titleLarge" style={{ color: theme.colors.primary }}>{t('companyDetails')}</Text>
          <ReportWorkCompanies attributions={dailyData.attributions} />
        </View> : <View style={styles.section}>
          <Title style={{ color: theme.colors.primary, fontSize: 20, fontWeight: '600' }}>
            {t('companyDetails')}
          </Title>
          <Divider style={[styles.divider, { backgroundColor: theme.colors.outline }]} />
          <List.Item
            title={t('companyName')}
            description={report.company_details.name || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={report.company_details.tax_identifier_kind === 'nif' ? t('nif') : t('nipc')}
            description={report.company_details.tax_identifier || report.company_details.nipc || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('address')}
            description={report.company_details.address || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('companyEmail')}
            description={report.company_details.email || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
        </View>}

        {/* Relatório Diário */}
        <View style={styles.section}>
          <Title style={{ color: theme.colors.primary, fontSize: 20, fontWeight: '600' }}>
            {t('dailyDrivingReport')} - {today}
          </Title>
          <Divider style={[styles.divider, { backgroundColor: theme.colors.outline }]} />
          {isReportRecordEditingEnabled() && (
            <Button mode="outlined" icon="pencil" onPress={() => setEditVisible(true)} style={{ marginBottom: 8 }}>
              {t('edit')}
            </Button>
          )}
          {anyEdited && (
            <Chip compact>{t('edited')}</Chip>
          )}
          <List.Item
            title={t('vehicleRegistration')}
            description={dailyData.vehicle_reg || t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('odometerStart')}
            description={dailyData.odometer_start !== null ? `${dailyData.odometer_start} km` : t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('odometerEnd')}
            description={dailyData.odometer_end !== null ? `${dailyData.odometer_end} km` : t('notProvided')}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('drivingTime')}
            description={`${dailyData.total_driving_time.hours}h ${dailyData.total_driving_time.minutes}m`}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('availabilityTime')}
            description={`${dailyData.total_availability_time.hours}h ${dailyData.total_availability_time.minutes}m`}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          <List.Item
            title={t('restTime')}
            description={`${dailyData.total_rest_time.hours}h ${dailyData.total_rest_time.minutes}m`}
            titleStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}
            descriptionStyle={{ color: theme.colors.onSurface }}
          />
          {dailyData.events.length > 0 && (
            <Surface
              elevation={1}
              style={[styles.tableContainer, { backgroundColor: theme.colors.surface }]}
              testID="daily-report-events"
            >
              <DataTable>
                <DataTable.Header style={{ backgroundColor: theme.colors.surfaceVariant }}>
                  <DataTable.Title textStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '600' }}>
                    {t('eventType')}
                  </DataTable.Title>
                  <DataTable.Title textStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '600' }}>
                    {t('start')}
                  </DataTable.Title>
                  <DataTable.Title textStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '600' }}>
                    {t('end')}
                  </DataTable.Title>
                  <DataTable.Title numeric textStyle={{ color: theme.colors.onSurfaceVariant, fontWeight: '600' }}>
                    {t('total')}
                  </DataTable.Title>
                </DataTable.Header>
                {dailyData.events.map((event, index) => (
                  <DataTable.Row
                    key={index}
                    testID={`daily-report-event-${index}`}
                    style={[styles.tableRow, { borderBottomColor: theme.colors.outline }]}
                  >
                    <DataTable.Cell textStyle={{ color: theme.colors.onSurface }}>
                      {event.event_type === 'driving' ? t('driving') : t('available')}
                      {event.company_name ? ` — ${event.company_name}` : ''}
                    </DataTable.Cell>
                    <DataTable.Cell textStyle={{ color: theme.colors.onSurface }}>
                      <View style={styles.timeLocationContainer}>
                        <Text style={{ color: theme.colors.onSurface }}>{event.start}</Text>
                        {event.start_location && (
                          <IconButton
                            icon="map-marker"
                            size={20}
                            onPress={() => openGoogleMaps(event.start_location)}
                            iconColor={theme.colors.primary}
                          />
                        )}
                      </View>
                    </DataTable.Cell>
                    <DataTable.Cell textStyle={{ color: theme.colors.onSurface }}>
                      <View style={styles.timeLocationContainer}>
                        <Text style={{ color: theme.colors.onSurface }}>{event.end}</Text>
                        {event.end_location && (
                          <IconButton
                            icon="map-marker"
                            size={20}
                            onPress={() => openGoogleMaps(event.end_location)}
                            iconColor={theme.colors.primary}
                          />
                        )}
                      </View>
                    </DataTable.Cell>
                    <DataTable.Cell numeric textStyle={{ color: theme.colors.onSurface }}>
                      {`${Math.floor(event.total / 60)}h ${event.total % 60}m`}
                    </DataTable.Cell>
                  </DataTable.Row>
                ))}
              </DataTable>
            </Surface>
          )}
          {dailyData.observations.length > 0 && (
            <>
              <List.Subheader style={{ color: theme.colors.onSurfaceVariant, fontWeight: '500' }}>
                {t('observations')}
              </List.Subheader>
              {dailyData.observations.map((obs, index) => (
                <List.Item
                  key={index}
                  title={obs}
                  titleStyle={{ color: theme.colors.onSurface }}
                  titleNumberOfLines={0}
                />
              ))}
            </>
          )}
        </View>
      </ScrollView>

      <FAB
        icon="file-download"
        label={t('exportPDF')}
        style={styles.fabExport}
        onPress={exportToPDF}
        mode="elevated"
      />

      {editVisible && (
        <EditDayEventsModal
          key={modalKey}
          visible={true}
          date={today}
                  onDismiss={() => {
          setEditVisible(false);
          setModalKey(prev => prev + 1);
        }}
          onSaved={() => fetchReport(report?.week_start)}
        />
      )}
    </Surface>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    paddingBottom: 80, // Espaço para FAB
  },
  section: {
    marginBottom: 24,
  },
  divider: {
    marginVertical: 12,
  },
  tableContainer: {
    marginVertical: 8,
    borderRadius: 8,
  },
  tableRow: {
    borderBottomWidth: 1,
  },
  timeLocationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  fabExport: {
    position: 'absolute',
    bottom: 32,
    right: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});